import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL, getAccessToken, getRefreshToken, saveTokens, clearTokens } from "@/lib/api";
import { flushQueue } from "@/lib/syncQueue";

export function useAuth() {
  // Open instantly with the cached account — server validation happens in the background
  const [user, setUser] = useState(() => {
    try {
      if (localStorage.getItem("access_token")) {
        const cached = JSON.parse(localStorage.getItem("cached_user") || "null");
        if (cached && cached.email) return cached;
      }
    } catch {}
    return null;
  });
  const wsRef = useRef(null);

  // Keep a local copy of the signed-in user so the app opens instantly offline
  useEffect(() => {
    if (user && user !== false) {
      try { localStorage.setItem("cached_user", JSON.stringify(user)); } catch {}
    }
  }, [user]);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAccessToken();
      if (!token) { setUser(false); return; }
      const openOfflineOrKeepWaiting = () => {
        // Server unreachable (offline / airplane mode) — NEVER log out. Use the cached account.
        try {
          const cached = JSON.parse(localStorage.getItem("cached_user") || "null");
          if (cached && cached.email) { setUser(cached); return; }
        } catch {}
        setUser(false);
      };
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        // Avoid re-render churn (and duplicate downstream fetches) when nothing changed
        setUser(prev => (prev && JSON.stringify(prev) === JSON.stringify(data)) ? prev : data);
        axios.post(`${API}/auth/cookie-sync`).catch(() => {});
        if (typeof data.auto_deduct === 'boolean') localStorage.setItem('lumber_auto_deduct', data.auto_deduct ? 'on' : 'off');
        try {
          const accounts = JSON.parse(localStorage.getItem('saved_accounts') || '[]');
          const idx = accounts.findIndex(a => a.email === data.email);
          const entry = { email: data.email, name: data.name || "", token: getAccessToken(), refresh_token: localStorage.getItem('refresh_token') };
          if (idx >= 0) accounts[idx] = entry; else accounts.push(entry);
          localStorage.setItem('saved_accounts', JSON.stringify(accounts));
        } catch {}
      } catch (err) {
        if (!err.response) { openOfflineOrKeepWaiting(); return; }
        // Server responded — access token expired. Try the refresh token before giving up.
        try {
          const rt = getRefreshToken();
          if (!rt) throw err;
          const { data: t } = await axios.post(`${API}/auth/refresh`, { refresh_token: rt }, { headers: { Authorization: `Bearer ${rt}` } });
          saveTokens(t.access_token, t.refresh_token || null);
          const { data } = await axios.get(`${API}/auth/me`);
          setUser(data);
          axios.post(`${API}/auth/cookie-sync`).catch(() => {});
        } catch (err2) {
          const st2 = err2.response?.status;
          if (st2 === 401 || st2 === 403 || (!getRefreshToken() && err2.response)) {
            // Server explicitly rejected the session — genuine sign-out
            clearTokens();
            localStorage.removeItem("cached_user");
            setUser(false);
          } else {
            openOfflineOrKeepWaiting();
          }
        }
      }
    };
    checkAuth();
    const handleExpired = () => { clearTokens(); localStorage.removeItem("cached_user"); setUser(false); };
    window.addEventListener("auth-expired", handleExpired);
    return () => window.removeEventListener("auth-expired", handleExpired);
  }, []);

  const handleLogout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    clearTokens();
    localStorage.removeItem("cached_user");
    setUser(false);
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
  }, []);

  return { user, setUser, handleLogout, wsRef };
}

export function useWebSocket(user, wsRef, onPalletTypesUpdated, onSavedListUpdated) {
  const lastLocalUpdate = useRef(0);

  useEffect(() => {
    if (!user || user === false) return;
    let ws = null;
    let reconnectTimer = null;
    let pingInterval = null;
    let retryCount = 0;

    const connectWs = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/ws-token`);
        const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws?token=' + data.token;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          retryCount = 0;
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send("ping");
          }, 25000);
        };

        ws.onmessage = (event) => {
          try {
            if (event.data === "pong") return;
            const msg = JSON.parse(event.data);
            if (msg.type === "pallet_types_updated") onPalletTypesUpdated();
            else if (msg.type === "settings_updated") {
              if (typeof msg.auto_deduct === 'boolean') localStorage.setItem('lumber_auto_deduct', msg.auto_deduct ? 'on' : 'off');
            }
            else if (msg.type === "saved_list_updated") {
              const timeSinceLocal = Date.now() - lastLocalUpdate.current;
              if (timeSinceLocal < 2500) { /* skip — local change is recent */ }
              else onSavedListUpdated();
            }
            else if (msg.type === "orders_updated") {
              window.dispatchEvent(new Event('orders-updated'));
            }
            else if (msg.type === "cut_lists_updated") {
              window.dispatchEvent(new Event('cut-lists-updated'));
            }
          } catch {}
        };

        ws.onclose = () => {
          if (pingInterval) clearInterval(pingInterval);
          const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
          retryCount++;
          reconnectTimer = setTimeout(connectWs, delay);
        };

        ws.onerror = () => { ws.close(); };
      } catch {
        const delay = Math.min(1000 * Math.pow(2, retryCount), 30000);
        retryCount++;
        reconnectTimer = setTimeout(connectWs, delay);
      }
    };
    connectWs();

    // Reconnect INSTANTLY when the network returns or the app wakes — no backoff wait
    const wakeReconnect = () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      retryCount = 0;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) connectWs();
    };
    const onVisible = () => { if (document.visibilityState === "visible") wakeReconnect(); };
    window.addEventListener("online", wakeReconnect);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.removeEventListener("online", wakeReconnect);
      document.removeEventListener("visibilitychange", onVisible);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingInterval) clearInterval(pingInterval);
      if (ws) ws.close();
      wsRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync on tab focus / app wake
  useEffect(() => {
    if (!user || user === false) return;
    const syncOnFocus = () => {
      onPalletTypesUpdated();
      onSavedListUpdated();
      // Orders were NOT refreshed on wake — a WS broadcast missed while the phone
      // slept meant a new order stayed invisible until manual action. Fixed here.
      window.dispatchEvent(new Event('orders-updated'));
      flushQueue();
    };
    const handleVisibility = () => { if (document.visibilityState === "visible") syncOnFocus(); };
    window.addEventListener("focus", syncOnFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", syncOnFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { lastLocalUpdate };
}
